export type ThemeMode = 'dark' | 'light';

// ─── Color Primitives ───────────────────────────────────────
const colors = {
  // Brand
  primary: '#ff6c00',
  primaryHover: { dark: '#ff8533', light: '#e56200' },
  primaryMuted: { dark: 'rgba(255,108,0,0.12)', light: 'rgba(255,108,0,0.08)' },

  // Success
  success: { dark: '#22c55e', light: '#16a34a' },
  successMuted: { dark: 'rgba(34,197,94,0.12)', light: 'rgba(22,163,74,0.08)' },

  // Danger
  danger: { dark: '#ef4444', light: '#dc2626' },
  dangerMuted: { dark: 'rgba(239,68,68,0.12)', light: 'rgba(220,38,38,0.08)' },

  // Warning
  warning: { dark: '#f59e0b', light: '#d97706' },
  warningMuted: { dark: 'rgba(245,158,11,0.12)', light: 'rgba(217,119,6,0.08)' },

  // Info
  info: { dark: '#3b82f6', light: '#2563eb' },
  infoMuted: { dark: 'rgba(59,130,246,0.12)', light: 'rgba(37,99,235,0.08)' },
} as const;

// ─── Theme Tokens ───────────────────────────────────────────
export interface ThemeTokens {
  // Surfaces
  base: string;
  surface: string;
  surfaceElevated: string;
  surfaceInset: string;

  // Borders
  border: string;
  borderSoft: string;
  borderEmphasis: string;

  // Brand
  primary: string;
  primaryHover: string;
  primaryMuted: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textMuted: string;

  // Semantic
  success: string;
  successMuted: string;
  danger: string;
  dangerMuted: string;
  warning: string;
  warningMuted: string;
  info: string;
  infoMuted: string;

  // Layout
  sidebarWidth: number;
  headerHeight: number;
  contentPadding: number;
  contentPaddingMobile: number;

  // Radius
  radiusSm: number;
  radiusMd: number;
  radiusLg: number;
  radiusXl: number;

  // Spacing (8px grid)
  spaceXs: number;
  spaceSm: number;
  spaceMd: number;
  spaceLg: number;
  spaceXl: number;

  // Type scale (px)
  fontCaption: number;
  fontLabel: number;
  fontBody: number;
  fontH4: number;
  fontH3: number;
  fontH2: number;
  fontH1: number;
  fontMetric: number;
}

const darkTokens: ThemeTokens = {
  // Surfaces
  base: '#0c0f14',
  surface: '#12161e',
  surfaceElevated: '#1a1f2a',
  surfaceInset: '#0e1118',

  // Borders
  border: 'rgba(255,255,255,0.06)',
  borderSoft: 'rgba(255,255,255,0.03)',
  borderEmphasis: 'rgba(255,255,255,0.12)',

  // Brand
  primary: colors.primary,
  primaryHover: colors.primaryHover.dark,
  primaryMuted: colors.primaryMuted.dark,

  // Text
  textPrimary: 'rgba(255,255,255,0.92)',
  textSecondary: 'rgba(255,255,255,0.55)',
  textMuted: 'rgba(255,255,255,0.32)',

  // Semantic
  success: colors.success.dark,
  successMuted: colors.successMuted.dark,
  danger: colors.danger.dark,
  dangerMuted: colors.dangerMuted.dark,
  warning: colors.warning.dark,
  warningMuted: colors.warningMuted.dark,
  info: colors.info.dark,
  infoMuted: colors.infoMuted.dark,

  // Layout
  sidebarWidth: 240,
  headerHeight: 56,
  contentPadding: 24,
  contentPaddingMobile: 16,

  // Radius
  radiusSm: 6,
  radiusMd: 10,
  radiusLg: 14,
  radiusXl: 20,

  // Spacing (8px grid)
  spaceXs: 4,
  spaceSm: 8,
  spaceMd: 16,
  spaceLg: 24,
  spaceXl: 32,

  // Type scale
  fontCaption: 11,
  fontLabel: 12,
  fontBody: 14,
  fontH4: 16,
  fontH3: 18,
  fontH2: 22,
  fontH1: 28,
  fontMetric: 32,
};

const darkCssVars: Record<string, string> = {
  '--color-surface': '#12161e',
  '--color-text-muted': 'rgba(255,255,255,0.32)',
  '--card-border': 'rgba(255,255,255,0.10)',
  '--card-border-hover': 'rgba(255,255,255,0.18)',
  '--card-radius': '10px',
  '--card-shadow': '0 1px 2px rgba(0,0,0,0.24)',
  '--table-border-header': 'rgba(255,255,255,0.08)',
  '--table-border-row': 'rgba(255,255,255,0.04)',
  '--table-row-hover': 'rgba(255,255,255,0.04)',
  '--table-row-active': 'rgba(255,255,255,0.07)',
};

const lightTokens: ThemeTokens = {
  // Surfaces
  base: '#f8f9fb',
  surface: '#ffffff',
  surfaceElevated: '#ffffff',
  surfaceInset: '#f1f3f5',

  // Borders
  border: 'rgba(0,0,0,0.06)',
  borderSoft: 'rgba(0,0,0,0.03)',
  borderEmphasis: 'rgba(0,0,0,0.12)',

  // Brand
  primary: colors.primary,
  primaryHover: colors.primaryHover.light,
  primaryMuted: colors.primaryMuted.light,

  // Text
  textPrimary: 'rgba(0,0,0,0.88)',
  textSecondary: 'rgba(0,0,0,0.55)',
  textMuted: 'rgba(0,0,0,0.32)',

  // Semantic
  success: colors.success.light,
  successMuted: colors.successMuted.light,
  danger: colors.danger.light,
  dangerMuted: colors.dangerMuted.light,
  warning: colors.warning.light,
  warningMuted: colors.warningMuted.light,
  info: colors.info.light,
  infoMuted: colors.infoMuted.light,

  // Layout
  sidebarWidth: 240,
  headerHeight: 56,
  contentPadding: 24,
  contentPaddingMobile: 16,

  // Radius
  radiusSm: 6,
  radiusMd: 10,
  radiusLg: 14,
  radiusXl: 20,

  // Spacing (8px grid)
  spaceXs: 4,
  spaceSm: 8,
  spaceMd: 16,
  spaceLg: 24,
  spaceXl: 32,

  // Type scale
  fontCaption: 11,
  fontLabel: 12,
  fontBody: 14,
  fontH4: 16,
  fontH3: 18,
  fontH2: 22,
  fontH1: 28,
  fontMetric: 32,
};

const lightCssVars: Record<string, string> = {
  '--color-surface': '#ffffff',
  '--color-text-muted': 'rgba(0,0,0,0.32)',
  '--card-border': 'rgba(0,0,0,0.08)',
  '--card-border-hover': 'rgba(0,0,0,0.16)',
  '--card-radius': '10px',
  '--card-shadow': '0 1px 2px rgba(0,0,0,0.05)',
  '--table-border-header': 'rgba(0,0,0,0.08)',
  '--table-border-row': 'rgba(0,0,0,0.04)',
  '--table-row-hover': 'rgba(0,0,0,0.04)',
  '--table-row-active': 'rgba(0,0,0,0.07)',
};

// ─── CSS Custom Properties ─────────────────────────────────
export function applyCssVars(mode: ThemeMode): void {
  const vars = mode === 'dark' ? darkCssVars : lightCssVars;
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

// ─── Token Accessor ─────────────────────────────────────────
export function getTokens(mode: ThemeMode): ThemeTokens {
  return mode === 'dark' ? darkTokens : lightTokens;
}

// ─── Style Generators ───────────────────────────────────────
// Borders are handled by CSS classes (.admin-card, .metric-block).
// These only provide background + radius — no border to avoid conflicts.
export function cardStyle(t: ThemeTokens): React.CSSProperties {
  return {
    background: t.surface,
    borderRadius: t.radiusMd,
  };
}

export function elevatedCardStyle(t: ThemeTokens): React.CSSProperties {
  return {
    background: t.surfaceElevated,
    borderRadius: t.radiusMd,
  };
}

export function insetStyle(t: ThemeTokens): React.CSSProperties {
  return {
    background: t.surfaceInset,
    borderRadius: t.radiusSm,
  };
}

// ─── Ant Design Theme Config ────────────────────────────────
export function getAntdThemeConfig(mode: ThemeMode) {
  const t = getTokens(mode);
  return {
    algorithm: mode === 'dark' ? 'dark' : 'default',
    token: {
      colorPrimary: t.primary,
      borderRadius: t.radiusSm,
      colorBgContainer: t.surface,
      colorBgLayout: t.base,
      colorBgElevated: t.surfaceElevated,
      colorBorder: t.border,
      colorText: t.textPrimary,
      colorTextSecondary: t.textSecondary,
      colorTextTertiary: t.textMuted,
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      fontSize: t.fontBody,
      controlHeight: 36,
      controlHeightSM: 32,
      controlHeightLG: 40,
      padding: 16,
      paddingSM: 12,
      paddingLG: 24,
      margin: 16,
      marginSM: 8,
      marginLG: 24,
    },
    components: {
      Button: {
        controlHeight: 36,
        controlHeightSM: 32,
        controlHeightLG: 40,
        borderRadius: t.radiusSm,
        fontWeight: 500,
      },
      Input: {
        controlHeight: 36,
        controlHeightSM: 32,
        borderRadius: t.radiusSm,
        colorBgContainer: t.surfaceInset,
      },
      Select: {
        controlHeight: 36,
        controlHeightSM: 32,
        borderRadius: t.radiusSm,
        colorBgContainer: t.surfaceInset,
      },
      Table: {
        headerBg: 'transparent',
        headerColor: t.textMuted,
        headerFontSize: t.fontLabel,
        rowHoverBg: t.surfaceElevated,
        borderColor: t.borderSoft,
        borderRadius: t.radiusMd,
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },
      Card: {
        colorBgContainer: t.surface,
        borderRadiusLG: t.radiusMd,
        paddingLG: 20,
      },
      Modal: {
        contentBg: t.surface,
        headerBg: t.surface,
        borderRadiusLG: t.radiusLg,
      },
      Drawer: {
        colorBgElevated: t.surfaceElevated,
      },
      Menu: {
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkItemSelectedBg: t.primaryMuted,
        darkItemSelectedColor: t.primary,
        itemBorderRadius: t.radiusSm,
        itemMarginInline: 8,
        itemHeight: 40,
      },
      Tabs: {
        inkBarColor: t.primary,
        itemActiveColor: t.primary,
        itemSelectedColor: t.primary,
        itemColor: t.textSecondary,
      },
      Tag: {
        borderRadiusSM: t.radiusSm,
      },
      Badge: {
        colorError: t.danger,
      },
    },
  };
}
